import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from './SEOHead';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import ArrowForward from '@mui/icons-material/ArrowForward';
import countriesNl from '../data/countries';
import countriesEn from '../data/countries.en';
import { useLanguage } from '../context/LanguageContext';
import { useT } from '../translations';

const CountryCard = ({ country, index = 0, t }) => {
	const navigate = useNavigate();

	return (
		<Paper
			elevation={0}
			className="animate-fade-up"
			style={{ '--delay': `${index * 75}ms` }}
			sx={{
				p: 3,
				borderRadius: 3,
				border: '1px solid',
				borderColor: 'grey.200',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				gap: 1.5,
				transition: 'box-shadow 0.28s ease, transform 0.28s ease',
				'&:hover': { boxShadow: 6, transform: 'translateY(-5px)' },
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
				<Typography fontSize={36} lineHeight={1}>
					{country.flag}
				</Typography>
				<Box>
					<Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
						{country.name}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{country.subtitle}
					</Typography>
				</Box>
				<Box sx={{ ml: 'auto', textAlign: 'right' }}>
					<Typography
						variant="h5"
						fontWeight="bold"
						sx={{ color: country.color, lineHeight: 1 }}
					>
						{country.taxRate}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{country.taxLabel}
					</Typography>
				</Box>
			</Box>

			<Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
				<Box sx={{ flex: 1 }}>
					<Typography variant="caption" fontWeight="bold" color="success.dark">
						{t('emigratePros')}
					</Typography>
					{country.pros.map((pro) => (
						<Typography key={pro} variant="caption" display="block" color="text.secondary">
							• {pro}
						</Typography>
					))}
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography variant="caption" fontWeight="bold" color="error.dark">
						{t('emigrateCons')}
					</Typography>
					{country.cons.map((con) => (
						<Typography key={con} variant="caption" display="block" color="text.secondary">
							• {con}
						</Typography>
					))}
				</Box>
			</Box>

			<Button
				size="small"
				endIcon={<ArrowForward />}
				onClick={() => navigate(`/emigreren/${country.slug}`)}
				sx={{ alignSelf: 'flex-start', mt: 'auto', textTransform: 'none' }}
			>
				{t('emigrateFullGuide')}
			</Button>
		</Paper>
	);
};

const EmigratePage = () => {
	const { language } = useLanguage();
	const t = useT(language);
	const countries = language === 'en' ? countriesEn : countriesNl;

	const emigrateDesc = language === 'en'
		? 'Compare popular expat destinations for Dutch retirees: Spain, Portugal, Dubai, Thailand, Cyprus, Singapore, Monaco and Switzerland. Tax rates and pros/cons at a glance.'
		: 'Vergelijk populaire emigratiebestemmingen voor Nederlanders: Spanje, Portugal, Dubai, Thailand, Cyprus, Singapore, Monaco en Zwitserland. Belastingtarieven en voor- en nadelen op een rij.';

	return (
		<Box sx={{ py: 4 }}>
			<SEOHead
				title={language === 'en'
					? 'Emigrate as a retiree | VisualiseermijnPensioen.nl'
					: 'Emigreren als gepensioneerde | VisualiseermijnPensioen.nl'}
				description={emigrateDesc}
				canonical="https://www.visualiseermijnpensioen.nl/emigreren"
				lang={language === 'en' ? 'en' : 'nl'}
			/>
			<Box className="animate-fade-up" sx={{ mb: 4, textAlign: 'center' }}>
				<Typography variant="h4" fontWeight="bold" gutterBottom>
					{t('emigrateTitle')}
				</Typography>
				<Typography variant="body1" color="text.secondary" sx={{ maxWidth: 580, mx: 'auto' }}>
					{t('emigrateSubtitle')}
				</Typography>
				<Chip
					className="animate-fade-up"
					style={{ '--delay': '0.2s' }}
					label={t('emigrateChip')}
					color="warning"
					sx={{ mt: 2, fontWeight: 600 }}
				/>
			</Box>

			<Grid container spacing={3}>
				{countries.map((country, index) => (
					<Grid key={country.name} size={{ xs: 12, md: 6 }}>
						<CountryCard country={country} index={index} t={t} />
					</Grid>
				))}
			</Grid>

			<Box
				sx={{
					mt: 4,
					p: 2.5,
					bgcolor: 'grey.50',
					borderRadius: 3,
					border: '1px solid',
					borderColor: 'grey.200',
				}}
			>
				<Typography variant="caption" color="text.secondary">
					{t('emigrateDisclaimer')}
				</Typography>
			</Box>
		</Box>
	);
};

export default EmigratePage;
